import React, { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts'
import { TrendingUp, PieChart as PieChartIcon, BarChart3, DollarSign } from 'lucide-react'

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4', '#84CC16']

const FinancialDataCharts = ({ financialStatements, formatCurrency }) => {
  // Debug logging
  console.log('FinancialDataCharts received data:', {
    statementsCount: financialStatements?.length || 0,
    statements: financialStatements?.slice(0, 2) // Log first 2 statements for structure
  })

  // Process financial data for visualizations
  const chartData = useMemo(() => {
    if (!financialStatements || financialStatements.length === 0) {
      console.log('No financial statements available')
      return null
    }

    // Get the latest P&L statement
    const latestPL = financialStatements
      .filter(s => s.statement_type === 'profit_loss')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]

    // Get the latest Balance Sheet
    const latestBS = financialStatements
      .filter(s => s.statement_type === 'balance_sheet')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]

    let processedData = {
      hasData: false,
      profitLoss: null,
      balanceSheet: null,
      revenueByProduct: null,
      profitabilityAnalysis: null,
      assetsLiabilitiesBreakdown: null
    }

    // Process P&L data - check both 'financial_data' and 'data' fields
    if (latestPL) {
      const plData = latestPL.financial_data || latestPL.data || {}
      console.log('Processing P&L data:', plData)
      processedData.hasData = true
      
      // Revenue by Product (Pie Chart) - Recharts format
      // Handle both snake_case (product_name) and camelCase (productName) field names
      if (plData.products && Array.isArray(plData.products)) {
        const productRevenues = plData.products
          .filter(p => (p.revenue && parseFloat(p.revenue) > 0))
          .map(p => ({
            name: p.product_name || p.productName || p.name || 'Unknown Product',
            value: parseFloat(p.revenue) || 0
          }))

        if (productRevenues.length > 0) {
          processedData.revenueByProduct = productRevenues
        }

        // Profitability Analysis (Bar Chart) - Recharts format
        // Handle both snake_case (cost_of_goods_sold, gross_profit) and camelCase (cogs, profit)
        const profitData = plData.products
          .filter(p => (parseFloat(p.revenue) || 0) > 0 || (parseFloat(p.cost_of_goods_sold || p.cogs) || 0) > 0)
          .map(p => ({
            name: p.product_name || p.productName || p.name || 'Unknown Product',
            revenue: parseFloat(p.revenue) || 0,
            cogs: parseFloat(p.cost_of_goods_sold || p.cogs) || 0,
            profit: parseFloat(p.gross_profit || p.profit) || (parseFloat(p.revenue) || 0) - (parseFloat(p.cost_of_goods_sold || p.cogs) || 0)
          }))

        if (profitData.length > 0) {
          processedData.profitabilityAnalysis = profitData
        }
      }

      // Store aggregated totals from P&L data
      processedData.profitLoss = {
        ...plData,
        totalRevenue: parseFloat(plData.total_revenue) || plData.products?.reduce((sum, p) => sum + (parseFloat(p.revenue) || 0), 0) || 0,
        totalCOGS: parseFloat(plData.total_cost_of_goods_sold) || plData.products?.reduce((sum, p) => sum + (parseFloat(p.cost_of_goods_sold || p.cogs) || 0), 0) || 0,
        totalGrossProfit: parseFloat(plData.total_gross_profit) || 0,
        totalOperatingExpenses: parseFloat(plData.total_operating_expenses) || 0,
        operatingIncome: parseFloat(plData.operating_income) || 0
      }
    }

    // Process Balance Sheet data - check both 'financial_data' and 'data' fields
    if (latestBS) {
      const bsData = latestBS.financial_data || latestBS.data || {}
      console.log('Processing Balance Sheet data:', bsData)
      processedData.hasData = true
      
      // Assets vs Liabilities (Pie Chart) - Recharts format
      const totalAssets = parseFloat(bsData.totalAssets) || 0
      const totalLiabilities = parseFloat(bsData.totalLiabilities) || 0
      const equity = parseFloat(bsData.equity) || (totalAssets - totalLiabilities)

      if (totalAssets > 0 || totalLiabilities > 0 || equity > 0) {
        const breakdownData = []
        
        if (totalAssets > 0) {
          breakdownData.push({ name: 'Total Assets', value: totalAssets })
        }
        if (totalLiabilities > 0) {
          breakdownData.push({ name: 'Total Liabilities', value: totalLiabilities })
        }
        if (equity > 0) {
          breakdownData.push({ name: 'Equity', value: equity })
        }

        if (breakdownData.length > 0) {
          processedData.assetsLiabilitiesBreakdown = breakdownData
        }
      }

      processedData.balanceSheet = bsData
    }

    console.log('Final processed chart data:', processedData)
    return processedData
  }, [financialStatements])

  if (!chartData || !chartData.hasData) {
    return (
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <PieChartIcon className="h-5 w-5 mr-2" />
              Financial Visualizations
            </h3>
          </div>
          <div className="card-body">
            <div className="text-center py-12">
              <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No financial data available for visualization</p>
              <p className="text-sm text-gray-500 mt-2">
                Submit your P&L and Balance Sheet to see charts and insights
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{label || payload[0]?.name}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color || entry.fill }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  // Custom label for pie chart
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  return (
    <div className="mb-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <TrendingUp className="h-6 w-6 mr-2" />
          Financial Data Visualizations
        </h2>
        <p className="text-gray-600 mt-1">
          Visual insights from your submitted financial statements
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Revenue by Product - Pie Chart */}
        {chartData.revenueByProduct && chartData.revenueByProduct.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <PieChartIcon className="h-5 w-5 mr-2" />
                Revenue by Product
              </h3>
              <p className="text-sm text-gray-500">Distribution of revenue across your products</p>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData.revenueByProduct}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.revenueByProduct.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Assets vs Liabilities - Pie Chart */}
        {chartData.assetsLiabilitiesBreakdown && chartData.assetsLiabilitiesBreakdown.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <PieChartIcon className="h-5 w-5 mr-2" />
                Financial Position
              </h3>
              <p className="text-sm text-gray-500">Breakdown of assets, liabilities, and equity</p>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData.assetsLiabilitiesBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.assetsLiabilitiesBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Profitability Analysis - Bar Chart */}
        {chartData.profitabilityAnalysis && chartData.profitabilityAnalysis.length > 0 && (
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Profitability Analysis by Product
              </h3>
              <p className="text-sm text-gray-500">Revenue, costs, and profit comparison across products</p>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData.profitabilityAnalysis}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(value) => formatCurrency(value)} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#3B82F6" name="Revenue" />
                  <Bar dataKey="cogs" fill="#EF4444" name="Cost of Goods Sold" />
                  <Bar dataKey="profit" fill="#10B981" name="Profit" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Key Financial Metrics Summary */}
      {(chartData.profitLoss || chartData.balanceSheet) && (
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {chartData.profitLoss && (
            <>
              <div className="card card-body">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(chartData.profitLoss.totalRevenue || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card card-body">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-red-100">
                    <TrendingUp className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total COGS</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(chartData.profitLoss.totalCOGS || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card card-body">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Gross Profit</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(chartData.profitLoss.totalGrossProfit || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card card-body">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Operating Income</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(chartData.profitLoss.operatingIncome || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {chartData.balanceSheet && (
            <>
              <div className="card card-body">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Assets</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(parseFloat(chartData.balanceSheet.totalAssets || chartData.balanceSheet.total_assets) || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card card-body">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-red-100">
                    <TrendingUp className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Liabilities</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(parseFloat(chartData.balanceSheet.totalLiabilities || chartData.balanceSheet.total_liabilities) || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card card-body">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Equity</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(parseFloat(chartData.balanceSheet.equity || chartData.balanceSheet.total_equity) || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default FinancialDataCharts
